import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import { useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import {
    Bookmark,
    FileText,
    KanbanSquare,
    LayoutDashboard,
    LogOut,
    Menu,
    Search,
    Settings,
    Shield,
    Sparkles
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { cms } from '@/lib/cms'
import { initials } from '@/lib/text'
import { useSignOut } from '@/hooks/use-sign-out'
import { Logo } from '@/components/generals/logo'
import { useSite } from '@/components/generals/site-context'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'

import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from '@/components/ui/sheet'

export interface NavItem {
    href: string
    label: string
    icon: LucideIcon
    exact?: boolean
}

export const CANDIDATE_NAV: Array<NavItem> = [
    { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { href: '/jobs', label: 'Explore jobs', icon: Search },
    { href: '/matches', label: 'My matches', icon: Sparkles },
    { href: '/resumes', label: 'My resumes', icon: FileText },
    { href: '/saved', label: 'Saved jobs', icon: Bookmark },
    { href: '/applications', label: 'Applications', icon: KanbanSquare },
    { href: '/settings', label: 'Settings', icon: Settings }
]

interface SidebarProps {
    items: Array<NavItem>
    heading?: string
    showAdminLink?: boolean
    onNavigate?: () => void
}

const SidebarNav: FC<SidebarProps> = ({ items, heading, showAdminLink = true, onNavigate }) => {
    const router = useRouter()
    const { viewer } = useSite()
    const { signOut, pending } = useSignOut()

    const isActive = (item: NavItem) => item.exact
        ? router.pathname === item.href
        : router.pathname === item.href || router.pathname.startsWith(`${item.href}/`)

    return (
        <div className='flex h-full flex-col gap-6 p-4'>
            <div className='px-2 pt-1'>
                <Logo />
            </div>

            <nav aria-label={heading ?? 'Workspace'} className='flex flex-col gap-1'>
                {heading && <p className='px-3 pb-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{heading}</p>}
                {items.map(item => {
                    const active = isActive(item)
                    return (
                        <Link
                            key={item.href}
                            href={item.href}
                            onClick={onNavigate}
                            aria-current={active ? 'page' : undefined}
                            className={cn(
                                'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                                active && 'bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary'
                            )}
                        >
                            <item.icon className='size-4.5' />
                            {item.label}
                        </Link>
                    )
                })}

                {viewer?.isAdmin && showAdminLink && (
                    <Link
                        href='/admin'
                        onClick={onNavigate}
                        className={cn(
                            'mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
                            router.pathname.startsWith('/admin') && 'bg-primary/10 text-primary'
                        )}
                    >
                        <Shield className='size-4.5' />
                        Admin workspace
                    </Link>
                )}
            </nav>

            {viewer && (
                <div className='mt-auto flex items-center gap-3 rounded-xl border border-border/70 bg-background p-3'>
                    <Avatar>
                        <AvatarFallback>{initials(viewer.name)}</AvatarFallback>
                    </Avatar>
                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-sm font-semibold' data-mdlfy-cms={cms('users', viewer.id, 'name')}>{viewer.name}</p>
                        <p className='truncate text-xs text-muted-foreground' data-mdlfy-cms={cms('users', viewer.id, 'email')}>{viewer.email}</p>
                    </div>
                    <Button
                        variant='ghost'
                        size='icon-sm'
                        aria-label='Sign out'
                        disabled={pending}
                        onClick={() => void signOut()}
                    >
                        <LogOut />
                    </Button>
                </div>
            )}
        </div>
    )
}

interface ShellProps {
    children: ReactNode
    items?: Array<NavItem>
    heading?: string
    showAdminLink?: boolean
}

/** Workspace layout with a sidebar on desktop and a slide-in menu on small screens. */
export const AppShell: FC<ShellProps> = ({ children, items = CANDIDATE_NAV, heading, showAdminLink = true }) => {
    const [open, setOpen] = useState(false)

    return (
        <div className='min-h-screen bg-canvas lg:pl-64'>
            <a
                href='#main'
                className='sr-only z-50 rounded-lg bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3'
            >
                Skip to content
            </a>

            <aside className='fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border/70 bg-sidebar lg:block'>
                <SidebarNav items={items} heading={heading} showAdminLink={showAdminLink} />
            </aside>

            <div className='sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border/70 bg-background/90 px-4 backdrop-blur lg:hidden'>
                <Button variant='ghost' size='icon' aria-label='Open menu' onClick={() => setOpen(true)}>
                    <Menu />
                </Button>
                <Logo />
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side='left' className='p-0'>
                    <SheetHeader className='sr-only'>
                        <SheetTitle>Navigation</SheetTitle>
                    </SheetHeader>
                    <SidebarNav items={items} heading={heading} showAdminLink={showAdminLink} onNavigate={() => setOpen(false)} />
                </SheetContent>
            </Sheet>

            <main id='main' className='min-w-0'>
                {children}
            </main>
        </div>
    )
}

interface PageHeaderProps {
    title: string
    description?: ReactNode
    actions?: ReactNode
    eyebrow?: ReactNode
}

export const PageHeader: FC<PageHeaderProps> = ({ title, description, actions, eyebrow }) => (
    <div className='flex flex-col gap-4 border-b border-border/70 bg-background px-4 py-6 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8'>
        <div className='flex min-w-0 flex-col gap-1.5'>
            {eyebrow}
            <h1 className='text-2xl font-bold text-foreground sm:text-[1.65rem]'>{title}</h1>
            {description && <p className='max-w-2xl text-sm text-muted-foreground'>{description}</p>}
        </div>
        {actions && <div className='flex flex-wrap items-center gap-2'>{actions}</div>}
    </div>
)

export const PageBody: FC<{ children: ReactNode, className?: string }> = ({ children, className }) => (
    <div className={cn('px-4 py-6 sm:px-6 lg:px-8', className)}>
        {children}
    </div>
)
